import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
} from "@mui/material";

import VisibilityOffOutlinedIcon
    from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon
    from "@mui/icons-material/VisibilityOutlined";

import {
    handleValidationResponse,
} from "../util/integration";

import {
    validatePassword as validatePasswordAPI,
} from "../api/validation_api";

import {
    VALIDATION,
} from "./../util/configuration";

/*
 * Structure: usePasswordValidation performs debounced backend validation,
 * usePasswordConfirmation checks both password fields, usePasswordDialog
 * manages dialog state and actions, and the remaining components render the UI.
 */


const STATUS = Object.freeze({
    idle: "idle",
    checking: "checking",
    valid: "valid",
    invalid: "invalid",
});

const MESSAGES = Object.freeze({
    title:
        "Passwort ändern",
    passwordLabel:
        "Neues Passwort",
    confirmationLabel:
        "Neues Passwort bestätigen",
    showPassword:
        "Passwort anzeigen",
    hidePassword:
        "Passwort ausblenden",
    showConfirmation:
        "Passwortbestätigung anzeigen",
    hideConfirmation:
        "Passwortbestätigung ausblenden",
    closeButton:
        "Schließen",
    saveButton:
        "Speichern",
    required:
        "Bitte ein neues Passwort eingeben.",
    invalid:
        "Das Passwort entspricht nicht den Vorgaben.",
    valid:
        "Das Passwort ist gültig.",
    checking:
        "Passwort wird geprüft …",
    checkFailed:
        "Das Passwort konnte nicht geprüft werden.",
    confirmationRequired:
        "Bitte das neue Passwort bestätigen.",
    mismatch:
        "Die Passwörter stimmen nicht überein.",
});

const EMPTY_HELPER_TEXT = " ";

const NOOP = () => {};

const validateWithBackend = (
    validator,
    value,
    showMessage,
) => {
    return new Promise((resolve) => {
        handleValidationResponse(
            validator(value),
            (response) => {
                resolve({
                    successful: true,
                    valid: Boolean(response),
                });
            },
            () => {
                resolve({
                    successful: false,
                    valid: false,
                });
            },
            showMessage,
        );
    });
};

const useBoolean = (
    initialValue = false,
) => {
    const [
        value,
        setValue,
    ] = useState(initialValue);

    const toggle =
        useCallback(() => {
            setValue((currentValue) => {
                return !currentValue;
            });
        }, []);

    const reset =
        useCallback(() => {
            setValue(initialValue);
        }, [initialValue]);

    return {
        reset,
        toggle,
        value,
    };
};

const usePasswordValidation = ({
    enabled,
    password,
    passwordValidator,
    showMessage,
}) => {
    const [
        status,
        setStatus,
    ] = useState(STATUS.idle);

    const [
        message,
        setMessage,
    ] = useState("");

    const validationVersionRef =
        useRef(0);

    const reset =
        useCallback(() => {
            validationVersionRef.current += 1;
            setStatus(STATUS.idle);
            setMessage("");
        }, []);

    useEffect(() => {
        validationVersionRef.current += 1;

        const validationVersion =
            validationVersionRef.current;

        if (!enabled) {
            setStatus(STATUS.idle);
            setMessage("");

            return undefined;
        }

        if (!password) {
            setStatus(STATUS.invalid);
            setMessage(MESSAGES.required);

            return undefined;
        }

        setStatus(STATUS.checking);
        setMessage(MESSAGES.checking);

        const timeoutId = window.setTimeout(
            async () => {
                const result =
                    await validateWithBackend(
                        passwordValidator,
                        password,
                        showMessage,
                    );

                const validationIsOutdated =
                    validationVersionRef.current
                    !== validationVersion;

                if (validationIsOutdated) {
                    return;
                }

                if (!result.successful) {
                    setStatus(STATUS.invalid);
                    setMessage(
                        MESSAGES.checkFailed,
                    );

                    return;
                }

                if (!result.valid) {
                    setStatus(STATUS.invalid);
                    setMessage(MESSAGES.invalid);

                    return;
                }

                setStatus(STATUS.valid);
                setMessage(MESSAGES.valid);
            },
            VALIDATION.validationDelay,
        );

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        enabled,
        password,
        passwordValidator,
        showMessage,
    ]);

    return {
        message,
        reset,
        status,
        valid: status === STATUS.valid,
    };
};

const usePasswordConfirmation = ({
    confirmation,
    password,
}) => {
    if (!confirmation) {
        return {
            message:
                MESSAGES.confirmationRequired,
            valid: false,
        };
    }

    if (confirmation !== password) {
        return {
            message:
                MESSAGES.mismatch,
            valid: false,
        };
    }

    return {
        message: "",
        valid: true,
    };
};

const usePasswordDialog = ({
    onClose,
    onSave,
    open,
    passwordValidator,
    showMessage,
}) => {
    const [
        password,
        setPassword,
    ] = useState("");

    const [
        confirmation,
        setConfirmation,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    const passwordVisibility =
        useBoolean(false);

    const confirmationVisibility =
        useBoolean(false);

    const validation =
        usePasswordValidation({
            enabled: open,
            password,
            passwordValidator,
            showMessage,
        });

    const confirmationValidation =
        usePasswordConfirmation({
            confirmation,
            password,
        });

    useEffect(() => {
        if (!open) {
            return;
        }

        setPassword("");
        setConfirmation("");
        setSaving(false);

        passwordVisibility.reset();
        confirmationVisibility.reset();
        validation.reset();
    }, [
        confirmationVisibility.reset,
        open,
        passwordVisibility.reset,
        validation.reset,
    ]);

    const canSave =
        validation.valid
        && confirmationValidation.valid
        && !saving;

    const handlePasswordChange =
        useCallback((event) => {
            setPassword(event.target.value);
        }, []);

    const handleConfirmationChange =
        useCallback((event) => {
            setConfirmation(
                event.target.value,
            );
        }, []);

    const save =
        useCallback(async () => {
            if (!canSave) {
                return;
            }

            setSaving(true);

            try {
                await onSave(password);
            } finally {
                setSaving(false);
            }
        }, [
            canSave,
            onSave,
            password,
        ]);

    const close =
        useCallback(() => {
            if (!saving) {
                onClose();
            }
        }, [
            onClose,
            saving,
        ]);

    const handleConfirmationKeyDown =
        useCallback((event) => {
            if (event.key !== "Enter") {
                return;
            }

            event.preventDefault();
            void save();
        }, [save]);

    return {
        canSave,
        close,
        confirmation,
        confirmationValidation,
        confirmationVisibility,
        handleConfirmationChange,
        handleConfirmationKeyDown,
        handlePasswordChange,
        password,
        passwordVisibility,
        save,
        saving,
        validation,
    };
};

const PasswordVisibilityAdornment = ({
    hideLabel,
    onToggle,
    showLabel,
    visible,
}) => {
    return (
        <InputAdornment position="end">
            <IconButton
                edge="end"
                onClick={onToggle}
                aria-label={
                    visible
                        ? hideLabel
                        : showLabel
                }
            >
                {
                    visible
                        ? (
                            <VisibilityOffOutlinedIcon />
                        )
                        : (
                            <VisibilityOutlinedIcon />
                        )
                }
            </IconButton>
        </InputAdornment>
    );
};

const PasswordValidationAlert = ({
    message,
    status,
}) => {
    if (status === STATUS.idle) {
        return null;
    }

    if (status === STATUS.checking) {
        return (
            <Alert
                severity="info"
                icon={
                    <CircularProgress
                        size={18}
                        color="inherit"
                    />
                }
            >
                {message}
            </Alert>
        );
    }

    return (
        <Alert
            severity={
                status === STATUS.valid
                    ? "success"
                    : "error"
            }
        >
            {message}
        </Alert>
    );
};

export default function EditPasswordDialog({
    appAPI,
    dialogTitle = MESSAGES.title,
    open,
    onClose,
    onSave,
    passwordValidator = validatePasswordAPI,
}) {
    const showMessage =
        appAPI?.showMessage
        ?? NOOP;

    const {
        canSave,
        close,
        confirmation,
        confirmationValidation,
        confirmationVisibility,
        handleConfirmationChange,
        handleConfirmationKeyDown,
        handlePasswordChange,
        password,
        passwordVisibility,
        save,
        saving,
        validation,
    } = usePasswordDialog({
        onClose,
        onSave,
        open,
        passwordValidator,
        showMessage,
    });

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {dialogTitle}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        label={MESSAGES.passwordLabel}
                        type={
                            passwordVisibility.value
                                ? "text"
                                : "password"
                        }
                        value={password}
                        disabled={saving}
                        autoComplete="new-password"
                        error={
                            validation.status
                            === STATUS.invalid
                        }
                        InputProps={{
                            endAdornment: (
                                <PasswordVisibilityAdornment
                                    visible={
                                        passwordVisibility.value
                                    }
                                    onToggle={
                                        passwordVisibility.toggle
                                    }
                                    showLabel={
                                        MESSAGES.showPassword
                                    }
                                    hideLabel={
                                        MESSAGES.hidePassword
                                    }
                                />
                            ),
                        }}
                        onChange={
                            handlePasswordChange
                        }
                    />

                    <PasswordValidationAlert
                        status={validation.status}
                        message={validation.message}
                    />

                    <TextField
                        fullWidth
                        label={
                            MESSAGES.confirmationLabel
                        }
                        type={
                            confirmationVisibility.value
                                ? "text"
                                : "password"
                        }
                        value={confirmation}
                        disabled={saving}
                        autoComplete="new-password"
                        error={
                            Boolean(confirmation)
                            && !confirmationValidation.valid
                        }
                        helperText={
                            confirmation
                                ? (
                                    confirmationValidation.message
                                    || EMPTY_HELPER_TEXT
                                )
                                : EMPTY_HELPER_TEXT
                        }
                        InputProps={{
                            endAdornment: (
                                <PasswordVisibilityAdornment
                                    visible={
                                        confirmationVisibility.value
                                    }
                                    onToggle={
                                        confirmationVisibility.toggle
                                    }
                                    showLabel={
                                        MESSAGES.showConfirmation
                                    }
                                    hideLabel={
                                        MESSAGES.hideConfirmation
                                    }
                                />
                            ),
                        }}
                        onChange={
                            handleConfirmationChange
                        }
                        onKeyDown={
                            handleConfirmationKeyDown
                        }
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={close}
                    disabled={saving}
                >
                    {MESSAGES.closeButton}
                </Button>

                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!canSave}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {MESSAGES.saveButton}
                </Button>
            </DialogActions>
        </Dialog>
    );
}